import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckSquare, Scissors, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@stockline/ui/components/alert-dialog";
import { cn } from "@/lib/utils";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import { useFlowSelectionContext } from "../contexts/FlowSelectionContext";
import { useRecapFlowBatchExtraActions } from "../contexts/RecapPrimitivesContext";
import { useSplitOrder } from "../hooks/useSplitOrder";
import {
  SELECTION_ISLAND_CHROME,
  selectionIslandMotion,
} from "./ui/selection-island-styles";

interface FlowBatchIslandProps {
  preOrder: PreOrder;
  flows: PreOrderFlow[];
  onFlowDelete: (flowId: string) => void;
  disabled?: boolean;
  /** When true the modify buffer has unsaved rows — split is blocked because
   *  buffer-only flow IDs don't exist in Dexie and updateFlow would no-op. */
  isModifying?: boolean;
  pendingChangeCount?: number;
  isFlushing?: boolean;
  onValidate?: () => Promise<void>;
}

const EXIT_MS = 150;

/**
 * Floating batch-action toolbar for flow-level selection in the focus view.
 * Appears when at least one flow is selected. Actions: select all, clear,
 * delete selected, split into new order.
 */
export function FlowBatchIsland({
  preOrder,
  flows,
  onFlowDelete,
  disabled,
  isModifying,
  pendingChangeCount,
  isFlushing,
  onValidate,
}: Readonly<FlowBatchIslandProps>) {
  const { t } = useTranslation();
  const selection = useFlowSelectionContext();
  const { splitOrder } = useSplitOrder();
  // Sibling-workspace slot (transport injects its batch sender/recipient
  // pickers here). `null` on /recap + /purchases → the pill is unchanged.
  const ExtraActions = useRecapFlowBatchExtraActions();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);

  const visibleFlowIdSet = useMemo(
    () => new Set(flows.map((f) => f.id)),
    [flows],
  );
  const allFlowIds = useMemo(() => flows.map((f) => f.id), [flows]);
  const visibleSelectedCount = useMemo(
    () => selection.selectedIds.filter((id) => visibleFlowIdSet.has(id)).length,
    [selection.selectedIds, visibleFlowIdSet],
  );
  const hasVisibleSelection = visibleSelectedCount > 0;
  const allSelected = flows.length > 0 && visibleSelectedCount === flows.length;
  const [shouldRender, setShouldRender] = useState(hasVisibleSelection);
  const lastCountRef = useRef(visibleSelectedCount);

  useEffect(() => {
    if (hasVisibleSelection) {
      setShouldRender(true);
      return;
    }
    setDeleteConfirmOpen(false);
    const timeout = setTimeout(() => setShouldRender(false), EXIT_MS);
    return () => clearTimeout(timeout);
  }, [hasVisibleSelection]);

  if (visibleSelectedCount > 0) {
    lastCountRef.current = visibleSelectedCount;
  }

  const handleSelectAll = useCallback(() => {
    selection.selectAll(allFlowIds);
  }, [selection, allFlowIds]);

  const handleDeleteSelected = useCallback(() => {
    const ids = selection.selectedIds.filter((id) => visibleFlowIdSet.has(id));
    for (const id of ids) {
      onFlowDelete(id);
    }
    selection.clear();
    setDeleteConfirmOpen(false);
    toast.success(
      t("ordersRecap.flowBatch.deleteSuccess", {
        count: ids.length,
        defaultValue: "{{count}} line(s) deleted",
      }),
    );
  }, [selection, onFlowDelete, visibleFlowIdSet, t]);

  const handleSplit = useCallback(async () => {
    if (isSplitting) return;
    // Intersect selection with currently visible flows to prune stale IDs
    // (rows deleted, synced away, or only in the modify buffer).
    const ids = selection.selectedIds.filter((id) => visibleFlowIdSet.has(id));
    if (ids.length === 0) return;
    // At least one flow must remain on the original order.
    if (ids.length >= flows.length) {
      toast.error(
        t(
          "ordersRecap.flowBatch.splitAllError",
          "Cannot split all lines — at least one must remain in the original order",
        ),
      );
      return;
    }
    setIsSplitting(true);
    try {
      await splitOrder(preOrder, ids);
      selection.clear();
    } catch (err) {
      console.error("[split-order] splitOrder failed:", err);
      toast.error(t("common.error"));
    } finally {
      setIsSplitting(false);
    }
  }, [
    isSplitting,
    selection,
    visibleFlowIdSet,
    flows.length,
    splitOrder,
    preOrder,
    t,
  ]);

  if (!shouldRender) return null;

  return (
    <>
      <div
        data-testid="flow-batch-island"
        aria-hidden={!hasVisibleSelection}
        inert={!hasVisibleSelection}
        className={cn(
          "pointer-events-auto flex w-fit max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-2xl",
          "2xl:inline-flex 2xl:flex-nowrap 2xl:rounded-full",
          "px-3 py-1.5",
          SELECTION_ISLAND_CHROME,
          "text-sm font-medium text-gray-700",
          selectionIslandMotion(hasVisibleSelection),
        )}
      >
        {/* Close / clear selection */}
        <button
          type="button"
          data-testid="flow-batch-clear"
          onClick={selection.clear}
          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          aria-label={t("ordersRecap.flowBatch.clear", "Clear selection")}
        >
          <X className="h-4 w-4" />
        </button>

        {/* Selected count badge */}
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold tabular-nums">
          {lastCountRef.current}
          <span className="font-normal">
            {t("ordersRecap.batchSelectedLabel", "selected")}
          </span>
        </span>

        <div className="hidden h-4 w-px bg-gray-200 2xl:block" aria-hidden />

        {/* Select all */}
        <button
          type="button"
          data-testid="flow-batch-select-all"
          onClick={handleSelectAll}
          disabled={allSelected}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs hover:bg-gray-100 transition-colors disabled:opacity-40"
        >
          <CheckSquare className="h-3.5 w-3.5" />
          {t("ordersRecap.flowBatch.selectAll", "Select all")}
        </button>

        {/* Sibling-workspace extra actions (transport sender/recipient pickers).
            Rendered between the selection controls and the destructive
            delete/split actions so the edit and the merge/split stay grouped. */}
        {ExtraActions ? (
          <>
            <div
              className="hidden h-4 w-px bg-gray-200 2xl:block"
              aria-hidden
            />
            <ExtraActions
              preOrder={preOrder}
              flows={flows}
              disabled={disabled}
              pendingChangeCount={pendingChangeCount}
              isFlushing={isFlushing}
              onValidate={onValidate}
            />
          </>
        ) : null}

        <div className="hidden h-4 w-px bg-gray-200 2xl:block" aria-hidden />

        {/* Delete selected */}
        <button
          type="button"
          data-testid="flow-batch-delete"
          onClick={() => setDeleteConfirmOpen(true)}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {t("ordersRecap.flowBatch.delete", "Delete")}
        </button>

        {/* Split into new order */}
        <button
          type="button"
          data-testid="flow-batch-split"
          onClick={handleSplit}
          disabled={disabled || isSplitting || isModifying}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-40"
          title={
            isModifying
              ? t(
                  "ordersRecap.flowBatch.splitDisabledModifying",
                  "Save before splitting",
                )
              : undefined
          }
        >
          <Scissors className="h-3.5 w-3.5" />
          {t("ordersRecap.flowBatch.splitOrder", "Split into new order")}
        </button>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ordersRecap.flowBatch.deleteConfirmTitle", "Delete lines")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ordersRecap.flowBatch.deleteConfirmDescription", {
                count: lastCountRef.current,
                defaultValue:
                  "Are you sure you want to delete {{count}} line(s)? This action cannot be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t("ordersRecap.flowBatch.deleteConfirm", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
